import { Button, Modal, Panel, Table } from '@elotech/components';
import { AxiosPromise } from 'axios';
import { Arquivo, ArquivoAgrupado, GrupoArquivo } from 'common/type';
import { useCallback, useMemo, useState } from 'react';

import { DocumentViewer } from '../document-viewer';
import { Informativo } from '../informativo';
import { TableContainer } from '../table';

type Props = {
  grupos: GrupoArquivo[];
  loading?: boolean;
  onDownload: (arquivo: Arquivo) => AxiosPromise;
  ordenarGrupos?: (primeiro: GrupoArquivo, segundo: GrupoArquivo) => number;
};

type ArquivoTabela = ArquivoAgrupado & {
  grupo: string;
};

const ordenarGruposPorOrdem = (primeiro: GrupoArquivo, segundo: GrupoArquivo) =>
  primeiro.ordem - segundo.ordem;

const ArquivoGroup = ({
  grupos,
  loading = false,
  onDownload,
  ordenarGrupos = ordenarGruposPorOrdem
}: Props) => {
  const [visualizandoArquivos, setVisualizandoArquivos] = useState(false);

  const arquivos = useMemo<ArquivoTabela[]>(
    () =>
      [...grupos].sort(ordenarGrupos).flatMap(grupo =>
        grupo.arquivos.map(arquivo => ({
          ...arquivo,
          grupo: grupo.descricao
        }))
      ),
    [grupos, ordenarGrupos]
  );

  const grupoPorArquivo = useMemo(
    () => new Map(arquivos.map(arquivo => [arquivo.arquivo, arquivo.grupo])),
    [arquivos]
  );

  const getDocumentCaption = useCallback(
    (arquivo: Arquivo) => grupoPorArquivo.get(arquivo),
    [grupoPorArquivo]
  );

  if (!loading && arquivos.length === 0) {
    return <Informativo type="INFO">Nenhum arquivo encontrado.</Informativo>;
  }

  return (
    <>
      {arquivos.length > 0 && (
        <Button
          type="button"
          color="neutral"
          className="mb-xs"
          onClick={() => setVisualizandoArquivos(true)}
        >
          <i className="fa fa-eye" /> Visualizar documentos
        </Button>
      )}
      <TableContainer>
        <Table<ArquivoTabela>
          values={arquivos}
          keyExtractor={arquivo => `${arquivo.origem}-${arquivo.id}`}
          loading={loading}
        >
          <Table.Column<ArquivoTabela>
            header="Nome do arquivo"
            value={arquivo => arquivo.arquivo.nome}
          />
          <Table.Column<ArquivoTabela>
            header="Grupo"
            value={arquivo => arquivo.grupo}
          />
          <Table.Column<ArquivoTabela>
            header="Sigiloso"
            value={arquivo => (arquivo.sigiloso ? 'Sim' : 'Não')}
          />
        </Table>
      </TableContainer>
      {visualizandoArquivos && (
        <Modal onClose={() => setVisualizandoArquivos(false)} maxWidth="90%">
          <Panel title="Visualizador de documentos">
            <DocumentViewer
              documents={arquivos.map(arquivo => arquivo.arquivo)}
              onDownload={onDownload}
              parentIsLoading={loading}
              getDocumentCaption={getDocumentCaption}
            />
          </Panel>
        </Modal>
      )}
    </>
  );
};

export default ArquivoGroup;
export { ArquivoGroup };
